import { useSpaceParameterOpenClause } from '@src/components/AppLayout/SpaceParameters';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_ASSET_TRANSACTION,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  ExportMenuItems,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  WSBlotterTableMaxRecordsReachedWarning,
  type AssetTransaction,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, GetMainMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { CustomerAssetTransactionsColumn } from './types';
import { useCustomerAssetTransactionsColumns } from './useCustomerAssetTransactionsColumns';
import {
  colIDToFilterBuilderKey,
  CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
  onlyServerFilterKeys,
  useCustomerAssetTransactionsFilter,
  type CustomerAssetTransactionsFilter,
} from './useCustomerAssetTransactionsFilter';

export interface CustomerAssetTransactionsBlotterTableProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: CustomerAssetTransactionsColumn[];
  defaultFilter?: CustomerAssetTransactionsFilter;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<AssetTransaction>) => void;
}

export function CustomerAssetTransactionsBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: CustomerAssetTransactionsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();
  const columns = useCustomerAssetTransactionsColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCustomerAssetTransactionsFilter({
    persistedBlotterTable,
  });

  const { clientSideFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const openClause = useSpaceParameterOpenClause(
    filterBuilderAccordion.openClause,
    CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT
  );

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause,
      mixpanel,
    });
  });

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams) => {
    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
    ]);
  });

  const request = useMemo<WebsocketRequest>(
    () => ({
      tag: blotterID,
      name: CUSTOMER_ASSET_TRANSACTION,
      sort_by: '-Timestamp',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, AssetTransaction>({
    request,
    rowID: 'TID' satisfies keyof AssetTransaction,
    clientLocalFilter: clientSideFilter,
    persistence: persistedBlotterTable,
    columns,
    getExtraMainMenuItems,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'CustomerAssetTransactions',
      tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.TransactTime} />
      <BlotterTable {...blotterTable} />
    </>
  );
}
